import React, { useEffect, useRef, useContext } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import TodoItemImage from "../../TodoList/TodoItemImage";
import assignmentPic from "../../../assets/images/course_pic.png";
import { CheckCircle } from "@mui/icons-material";
import IltClassInfo from "../../IltClass/IltClassInfo";
import { formatDate, daysBeforeToday } from "../../../utils/datetime";
import { useTranslation } from "react-i18next";
import WindowSizeContext from "../../../context/WindowSizeContext";
import { makeGetEntities } from "../../../redux/selectors/selectors";
import { useSelector } from "react-redux";
import CourseActionButton from "../../Buttons/CourseActionButton";
import UserAssignmentContext from "../../../context/UserAssignmentContext";
import { courseTypes } from "../../../constants";

const selector = makeGetEntities();

const useStyles = makeStyles(theme => ({
  button: {
    width: 127,
    [theme.breakpoints.down("sm")]: {
      width: "100%"
    }
  },
  checkIcon: {
    marginLeft: theme.spacing(1)
  }
}));

const GetILTButton = ({
  course,
  userAssignment,
  isEnrolled,
  openInPersonAssignmentModal
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { xsDown } = useContext(WindowSizeContext);
  const {
    onStartCourse,
    isStarted,
    onCreateUserAssignment,
    onResumeAssignment
  } = useContext(UserAssignmentContext);

  let disabled = false;

  const hasPrereqs = course =>
    Array.isArray(course.prerequisites) && course.prerequisites.length > 0;

  disabled = hasPrereqs(course) || course.isInstructorForCourse;

  const handlePlayAssignment = () => {
    if (course.typeName?.toLowerCase() === courseTypes.ilt.toLowerCase()) {
      openInPersonAssignmentModal(course);
    } else {
      onResumeAssignment(userAssignment);
    }
  };

  const handleEnrollClick = () => {
    if (
      course.typeName?.toLowerCase() === courseTypes.ilt.toLowerCase() &&
      userAssignment?.enrolledTrainingObjectId == course.id
    ) {
      openInPersonAssignmentModal(course);
    } else {
      onStartCourse(course);
    }
  };

  let button = (
    <>
      <Box display="flex" mb={1}>
        {isEnrolled && !disabled && (
          <Typography className="emphasized" color="primary" variant="caption">
            {t("upcomingIltWidget.youAreEnrolled")}
          </Typography>
        )}
        {isEnrolled && !xsDown && !disabled && (
          <CheckCircle
            color="primary"
            fontSize="small"
            className={classes.checkIcon}
          />
        )}
      </Box>
      <CourseActionButton
        isEnrolled={isEnrolled}
        startDate={course.zonedStartDate}
        endDate={course.zonedEndDate}
        isAssigned={!!userAssignment}
        isStarted={isStarted(userAssignment)}
        courseType={course.courseType}
        onEnrollClick={() => handleEnrollClick()}
        onResumeCourseClick={() => handlePlayAssignment()}
        onAddToCoursesClick={() => onCreateUserAssignment(course)}
        isInstructorForCourse={course.isInstructorForCourse}
        prereqs={course.prerequisites || []}
        hasNoFreeSeats={course.noFreeSeats}
        iltClassType={course.typeName}
        className={classes.button}
        isForUpcomingIltWidget
      />
      <AssignmentDueLabel
        assignment={userAssignment}
        courseId={course.id}
        isExcluded={course.isExcluded}
      />
    </>
  );

  return (
    <Box display="flex" width="100%" flexDirection="column" alignItems="center">
      {button}
    </Box>
  );
};

const AssignmentActions = ({
  course,
  openInPersonAssignmentModal,
  userAssignment
}) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
    >
      <GetILTButton
        userAssignment={userAssignment}
        course={course}
        isEnrolled={
          userAssignment && userAssignment.enrolledTrainingObjectId == course.id
        }
        openInPersonAssignmentModal={openInPersonAssignmentModal}
      />
    </Box>
  );
};

const AssignmentDueLabel = ({ assignment, courseId, isExcluded }) => {
  const { t } = useTranslation();
  if (
    assignment &&
    (!assignment.enrolledTrainingObject ||
      (assignment.enrollmentStatus === "admin" &&
        courseId !== assignment.enrolledTrainingObject.id)) &&
    !isExcluded
  ) {
    return (
      <Box display="flex" mt={1} data-qa="upcoming-ilt-assignment-due-label">
        <Typography color="textSecondary" variant="caption">
          {`${t("upcomingIltWidget.assignmentDue")} ${formatDate(
            assignment.dueDate
          )}`}
        </Typography>
      </Box>
    );
  } else {
    return null;
  }
};

function UpcomingIltItem(props) {
  const { courseId, openInPersonAssignmentModal, onHeightChange } = props;
  const iltInfoRef = useRef();
  // TODO: This needs to be memoized
  const course = useSelector(({ userAssignmentsListState }) => {
    return selector(userAssignmentsListState, {
      type: "version",
      id: courseId
    });
  });

  const isNew = () => {
    return daysBeforeToday(course.createdAt) <= 7;
  };

  useEffect(() => {
    if (iltInfoRef.current) {
      onHeightChange(iltInfoRef.current.getBoundingClientRect().height);
    }
  }, []);

  const actions = (
    <AssignmentActions
      userAssignment={course?.userAssignment}
      course={course}
      openInPersonAssignmentModal={openInPersonAssignmentModal}
    />
  );

  return (
    <>
      <Box
        display="flex"
        flex={1}
        flexDirection={{ xs: "column", sm: "row" }}
        flexWrap="wrap"
        justifyContent="center"
        ref={iltInfoRef}
      >
        <Box display="flex" flex={1} alignItems="flex-start" minWidth={272}>
          <Box
            ml={{ xs: 0, sm: 2 }}
            display="flex"
            flexDirection="column"
            mr={{ xs: 1.5, sm: 3 }}
            maxWidth={{ xs: 90, sm: "unset" }}
          >
            <TodoItemImage
              imageSrc={course.image || assignmentPic}
              isNew={isNew()}
              width={86}
            />
            <Box display={{ xs: "flex", sm: "none" }} mt={1}>
              {actions}
            </Box>
          </Box>
          {course && <IltClassInfo course={course} showCourseTypeIcon={true} />}
        </Box>
        <Box
          display={{ xs: "none", sm: "flex" }}
          alignItems="center"
          mx={2}
          justifyContent="center"
          my={2}
          width={128}
        >
          {actions}
        </Box>
      </Box>
    </>
  );
}

UpcomingIltItem.propTypes = {
  courseId: PropTypes.string,
  onHeightChange: PropTypes.func,
  openInPersonAssignmentModal: PropTypes.func
};

export default UpcomingIltItem;
